"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PhotoIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import PhotoCoachExampleModal, {
  type PhotoCoachExampleSlide,
} from "@/components/listing/PhotoCoachExampleModal";
import {
  PHOTO_COACH_EXAMPLES,
  resolvePhotoCoachExampleUrl,
  type PhotoCoachExample,
} from "@/lib/listing/photoCoachExamples";

type PhotoCoachExampleFrameProps = {
  baseName: string;
  alt: string;
  variant: "good" | "bad";
  resolvedUrl?: string | null;
  isResolving?: boolean;
  onOpen?: () => void;
};

function PhotoCoachExampleFrame({
  baseName,
  alt,
  variant,
  resolvedUrl,
  isResolving = false,
  onOpen,
}: PhotoCoachExampleFrameProps) {
  const isGood = variant === "good";
  const borderClass = isGood ? "border-primary-200" : "border-rose-200";
  const badgeClass = isGood
    ? "bg-primary-100 text-primary-800"
    : "bg-rose-100 text-rose-800";
  const BadgeIcon = isGood ? CheckCircleIcon : XCircleIcon;
  const canOpen = Boolean(resolvedUrl && onOpen);

  if (isResolving) {
    return (
      <div
        className={`flex aspect-[4/3] animate-pulse items-center justify-center rounded-lg border ${borderClass} bg-surface/70`}
        aria-hidden
      />
    );
  }

  if (!resolvedUrl) {
    return (
      <div
        className={`flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-lg border border-dashed ${borderClass} bg-surface/70 px-3 text-center`}
      >
        <PhotoIcon className="h-8 w-8 text-text-light" aria-hidden />
        <p className="text-xs text-text-light">
          Example image — add{" "}
          <span className="font-mono text-[11px]">{baseName}</span> to{" "}
          <span className="font-mono text-[11px]">public/images/</span>
        </p>
      </div>
    );
  }

  const frame = (
    <div
      className={`relative aspect-[4/3] overflow-hidden rounded-lg border ${borderClass} bg-secondary`}
    >
      <span
        className={`absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeClass}`}
      >
        <BadgeIcon className="h-3.5 w-3.5" aria-hidden />
        {isGood ? "Good" : "Avoid"}
      </span>
      {/* eslint-disable-next-line @next/next/no-img-element -- static public assets */}
      <img
        src={resolvedUrl}
        alt={alt}
        className="h-full w-full object-contain"
      />
    </div>
  );

  if (!canOpen) {
    return frame;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg text-left transition hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      aria-label={`View full-size ${alt}`}
    >
      {frame}
    </button>
  );
}

type PhotoCoachExampleCardProps = {
  example: PhotoCoachExample;
  exampleIndex: number;
  resolvedUrl?: string | null;
  isResolving?: boolean;
  onOpen: (exampleIndex: number) => void;
};

function PhotoCoachExampleCard({
  example,
  exampleIndex,
  resolvedUrl,
  isResolving,
  onOpen,
}: PhotoCoachExampleCardProps) {
  const label = example.variant === "good" ? "Good" : "Avoid";

  return (
    <article
      className="flex w-[min(72vw,13.5rem)] flex-shrink-0 snap-start flex-col gap-2"
      aria-label={`${example.slotTitle} — ${label} example`}
    >
      <p className="text-sm font-medium leading-snug text-foreground">
        {example.slotTitle}
      </p>
      <PhotoCoachExampleFrame
        baseName={example.baseName}
        alt={`${label} example: ${example.slotTitle}`}
        variant={example.variant}
        resolvedUrl={resolvedUrl}
        isResolving={isResolving}
        onOpen={() => onOpen(exampleIndex)}
      />
      <p className="text-xs leading-relaxed text-text-light">
        {example.description}
      </p>
    </article>
  );
}

export default function PhotoCoachExamples() {
  const [expanded, setExpanded] = useState(false);
  const [resolvedUrls, setResolvedUrls] = useState<
    Record<string, string | null>
  >({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const isResolving =
    expanded &&
    PHOTO_COACH_EXAMPLES.some((example) => !(example.id in resolvedUrls));

  useEffect(() => {
    if (!expanded) {
      return;
    }

    const pendingExamples = PHOTO_COACH_EXAMPLES.filter(
      (example) => !(example.id in resolvedUrls),
    );
    if (!pendingExamples.length) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const entries = await Promise.all(
        pendingExamples.map(async (example) => {
          const url = await resolvePhotoCoachExampleUrl(example.baseName);
          return [example.id, url] as const;
        }),
      );

      if (cancelled) {
        return;
      }

      setResolvedUrls((previous) => ({
        ...previous,
        ...Object.fromEntries(entries),
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [expanded, resolvedUrls]);

  const modalSlides = useMemo((): PhotoCoachExampleSlide[] => {
    return PHOTO_COACH_EXAMPLES.flatMap((example) => {
      const url = resolvedUrls[example.id];
      if (!url) {
        return [];
      }
      return [{ example, url }];
    });
  }, [resolvedUrls]);

  const openModalAt = (exampleIndex: number) => {
    const slideIndex = modalSlides.findIndex(
      (slide) => slide.example.id === PHOTO_COACH_EXAMPLES[exampleIndex]?.id,
    );
    if (slideIndex < 0) {
      return;
    }
    setModalIndex(slideIndex);
    setModalOpen(true);
  };

  return (
    <>
      <div className="rounded-md border border-primary-100 bg-surface/80">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-medium text-primary-900 hover:bg-primary-50/80"
          aria-expanded={expanded}
          aria-controls="seller-photo-coach-examples"
        >
          <span>See good vs bad examples</span>
          {expanded ? (
            <ChevronUpIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
          ) : (
            <ChevronDownIcon className="h-5 w-5 flex-shrink-0" aria-hidden />
          )}
        </button>

        {expanded ? (
          <div
            id="seller-photo-coach-examples"
            className="border-t border-primary-100 px-3 py-3"
          >
            <p className="mb-3 text-xs text-text-light">
              Swipe sideways to compare good and bad shots. Tap an image to view
              it full size.
            </p>
            <div
              className="-mx-1 overflow-x-auto overscroll-x-contain scroll-smooth px-1 pb-1"
              tabIndex={0}
              aria-label="Good and bad photo examples"
            >
              <div className="flex w-max min-w-full snap-x snap-mandatory gap-3">
                {PHOTO_COACH_EXAMPLES.map((example, index) => (
                  <PhotoCoachExampleCard
                    key={example.id}
                    example={example}
                    exampleIndex={index}
                    resolvedUrl={resolvedUrls[example.id]}
                    isResolving={isResolving && !(example.id in resolvedUrls)}
                    onOpen={openModalAt}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <PhotoCoachExampleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        slides={modalSlides}
        currentIndex={modalIndex}
        onIndexChange={setModalIndex}
      />
    </>
  );
}
